import Keycloak from "keycloak-js"
import { UserInfo } from "./UserInfo.js"

const TOKEN_STORAGE_KEY = 'vls_kc_token'
const REFRESH_TOKEN_STORAGE_KEY = 'vls_kc_refreshToken'
const ID_TOKEN_STORAGE_KEY = 'vls_kc_idToken'

export class Login {
	_keycloak
	_errorCount = 0
	_user
	_appContext
	_updateTokenInterval
	_onLoginHandlers = []
	_onLogoutHandlers = []

	init = () => {
		return new Promise((resolve, reject) => {
			// keycloak-js comes from npm: Keycloak 26 no longer serves /js/keycloak.js, and
			// without that script tag the server URL has to be passed explicitly as 'url'.
			const keycloak = new Keycloak({
				url: 'https://login.fairweben.de',
				realm: 'fairweben',
				clientId: 'vls',
			})
			const silentCheckSsoUri = window.location.origin + '/silent-check-sso.html'
			// Pass along a previously persisted refresh token (see _persistTokens) so a
			// reload restores the session via a direct refresh-token grant against Keycloak,
			// instead of depending on the browser's short-lived SSO cookie (check-sso only
			// runs as fallback when there's no stored token yet). checkLoginIframe is
			// disabled so a stored token forces that direct refresh call (updateToken(-1))
			// rather than a cookie-based iframe check - this is what lets the session
			// survive as long as the offline_access refresh token itself is valid.
			const storedTokens = this._readPersistedTokens()
			keycloak.init({
				onLoad: 'check-sso',
				silentCheckSsoRedirectUri: silentCheckSsoUri,
				checkLoginIframe: false,
				...storedTokens,
			})
				.then(authenticated => {
					this._keycloak = keycloak
					// Page loaded with valid authentication
					if (authenticated) {
						console.debug('Authenticated: ', this._keycloak)
						this._user = {
							email: this._keycloak.tokenParsed.email,
							firstName: this._keycloak.tokenParsed.given_name,
							lastName: this._keycloak.tokenParsed.family_name,
							roles: this._keycloak.tokenParsed.realm_access.roles,
							groups: this._keycloak.tokenParsed.groups,
							username: this._keycloak.tokenParsed.preferred_username
						}
						this._userInfo = new UserInfo('userInfoDlg', this._user)
						this._persistTokens()

						// Establish token refresh
						clearInterval(this._updateTokenInterval)
						this._updateTokenInterval = setInterval(() => this._updateToken(), 30000)
						// Execute session start operations
						this._onLoginHandlers.forEach(handler => handler(this._getValidTokenPromise, this._user))
						this._updateContext(true)
					}
					else {
						console.log('Not authenticated: ', this._keycloak)
						this._user = null
						this._clearPersistedTokens()
						this._updateContext(false)
					}
					resolve(authenticated)
				})
				.catch(e => {
					console.error('Failed to initialize! ', e)
					// Stored tokens may be stale/invalid (e.g. offline session expired
					// server-side) - drop them so the next attempt falls back to check-sso.
					this._clearPersistedTokens()
					reject(e)
				})
		})
	}

	_readPersistedTokens = () => {
		try {
			const token = localStorage.getItem(TOKEN_STORAGE_KEY)
			const refreshToken = localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY)
			const idToken = localStorage.getItem(ID_TOKEN_STORAGE_KEY)
			if (!token || !refreshToken) return {}
			return { token, refreshToken, idToken: idToken || undefined }
		}
		catch (e) {
			console.warn('Failed to read persisted OAuth tokens: ', e)
			return {}
		}
	}

	_persistTokens = () => {
		try {
			localStorage.setItem(TOKEN_STORAGE_KEY, this._keycloak.token)
			localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, this._keycloak.refreshToken)
			if (this._keycloak.idToken) {
				localStorage.setItem(ID_TOKEN_STORAGE_KEY, this._keycloak.idToken)
			}
		}
		catch (e) {
			console.warn('Failed to persist OAuth tokens: ', e)
		}
	}

	_clearPersistedTokens = () => {
		try {
			localStorage.removeItem(TOKEN_STORAGE_KEY)
			localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY)
			localStorage.removeItem(ID_TOKEN_STORAGE_KEY)
		}
		catch (e) {
			console.warn('Failed to clear persisted OAuth tokens: ', e)
		}
	}

	setAppContext = appContext => this._appContext = appContext

	addOnLoginHandlers = (...onLoginHandlers) => this._onLoginHandlers.push(...onLoginHandlers)

	addOnLogoutHandlers = (...onLogoutHandlers) => this._onLogoutHandlers.push(...onLogoutHandlers)

	logout = () => {
		// Run before the redirect below tears down the page (WS close etc.)
		this._onLogoutHandlers.forEach(handler => handler())
		if (this._keycloak) {
			this._keycloak.logout()
		}
		this._user = null
		this._clearPersistedTokens()
	}

	accountManagement = () => {
		if (this._keycloak) {
			this._keycloak.accountManagement()
		}
	}

	getUser = () => this._user

	setVisible = visible => {
		if (visible) {
			if (!this._keycloak) {
				this.init()
					.then(authenticated => {
						if (!authenticated) {
							this._keycloak.login({
								scope: 'openid offline_access',
							})
						}
					})
			}
			else if (!this._user) {
				this._keycloak.login({
					scope: 'openid offline_access',
				})
			}
		}
	}

	_updateToken = () => {
		let isTokenExpired
		try {
			isTokenExpired = this._keycloak.isTokenExpired(30)
		}
		catch (e) {
			console.warn(new Date(), 'Failed to check OAuth token validity!', e)
			clearInterval(this._updateTokenInterval)
			this._clearPersistedTokens()
			this._updateContext(false)
			return
		}
		if (isTokenExpired) {
			this._keycloak.updateToken(40)
				.then(() => {
					console.debug(new Date(), 'OAuth token refreshed.', this._keycloak)
					this._errorCount = 0
					this._persistTokens()
				})
				.catch(e => {
					console.warn(new Date(), 'Failed to refresh expired OAuth token!', e)
					this._errorCount++
					if (this._errorCount > 10) {
						clearInterval(this._updateTokenInterval)
						this._clearPersistedTokens()
						this._updateContext(false)
					}
				})
		}
		else {
			// console.debug(new Date(), 'OAuth token still valid.')
		}
	}

	_getValidTokenPromise = () => {
		return this._keycloak.updateToken(20)
			.then(updated => {
				if (updated) {
					console.debug(new Date(), 'Token had to be updated before call!')
					this._persistTokens()
				}
				return this._keycloak.token
			})
			.catch(e => {
				console.error('Failed to get a valid access Token!', e)
				clearInterval(this._updateTokenInterval)
				this._clearPersistedTokens()
				this._updateContext(false)
				throw e
			})
	}

	_updateContext = loggedIn => {
		if (loggedIn) {
			this._appContext.setOpen('counter', true)
			this._appContext.setOpen('login', false)
			this._appContext.setOpen('logout', true)
			this._appContext.setOpen('keycloakAccount', true)
			this._appContext.setOpen('userInfo', true)
			this._appContext.setActive('counter')
		}
		else {
			this._appContext.setOpen('counter', false)
			this._appContext.setOpen('logout', false)
			this._appContext.setOpen('keycloakAccount', false)
			this._appContext.setOpen('userInfo', false)
			this._appContext.setOpen('login', true)
			this._appContext.setActive('aboutUs')
		}
	}

}